/**
 * Live-Sync fuer den Viewer im Draft-Modus.
 *
 * Wird der Viewer mit `?mode=draft&token=...` geoeffnet (Editor-Play-Button),
 * verbindet er sich mit der WS-Session des Spots und horcht auf
 * `scene_draft_updated` / `node_graph_draft_updated` Notifications.
 *
 * Bei einem Update wird (debounced) die Seite neu geladen, sodass der
 * Scene-Builder den frischen `/scene/draft`-Stand erneut faehrt.
 *
 * Token-Lifecycle: Der initiale Token kommt aus der URL. Das `session-token`-
 * Modul refresht ihn periodisch und benachrichtigt uns per `onTokenChange`.
 * Wir reagieren mit einem sauberen Reconnect, sodass das WS mit dem frischen
 * Bearer erneut authentifiziert wird (`type: "auth"` kennt der Server nur
 * einmal pro Verbindung).
 */

import {
  getConfiguredCmsOrigin,
  shouldUseCmsDevProxy,
} from "./cms-origin";
import { getCurrentToken, getMode, onTokenChange } from "./session-token.js";

const RELOAD_DEBOUNCE_MS = 1500;
const HEARTBEAT_MS = 30_000;
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_ATTEMPTS = 10;

function generateSessionId(): string {
  return `viewer_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Baut die WebSocket-URL fuer die Spot-Session. Im Dev-Modus mit
 * Vite-Proxy wird auf den lokalen Vite-Origin gemapped (Proxy-Pfad
 * `/__cms` -> CMS-Backend), sonst direkt auf das CMS-Origin.
 */
function buildWebSocketUrl(spotId: string): string {
  if (shouldUseCmsDevProxy()) {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/__cms/ws/session/${encodeURIComponent(spotId)}`;
  }
  const origin = getConfiguredCmsOrigin();
  const wsOrigin = origin.replace(/^https:/, "wss:").replace(/^http:/, "ws:");
  return `${wsOrigin}/ws/session/${encodeURIComponent(spotId)}`;
}

type SessionMessage = {
  type?: string;
  status?: string;
  error?: string;
};

/**
 * Startet Live-Sync wenn der Viewer im Draft-Mode laeuft. No-op sonst.
 */
export function startLiveSync(
  spotId: string,
  onUpdate?: (reason: string) => void
): { close: () => void } | null {
  if (getMode() !== "draft") return null;
  const initialToken = getCurrentToken();
  if (!initialToken) return null;

  const sessionId = generateSessionId();
  let ws: WebSocket | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let reloadTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectAttempts = 0;
  let isClosed = false;
  let pendingReason: string | null = null;
  let unsubscribeTokenChange: (() => void) | null = null;

  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: "heartbeat", timestamp: Date.now() }));
        } catch (err) {
          console.warn("[LiveSync] Heartbeat send failed:", err);
        }
      }
    }, HEARTBEAT_MS);
  };

  const triggerUpdate = (reason: string) => {
    pendingReason = reason;
    if (reloadTimer) clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
      reloadTimer = null;
      const why = pendingReason;
      pendingReason = null;
      if (why == null) return;
      console.log(`[LiveSync] Applying live update (${why})`);
      if (typeof onUpdate === "function") {
        onUpdate(why);
      } else {
        window.location.reload();
      }
    }, RELOAD_DEBOUNCE_MS);
  };

  const scheduleReconnect = () => {
    if (isClosed) return;
    if (reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      console.warn("[LiveSync] Max reconnect attempts reached, giving up");
      return;
    }
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempts, 15_000);
    reconnectAttempts += 1;
    console.log(`[LiveSync] Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);
    setTimeout(connect, delay);
  };

  const connect = () => {
    if (isClosed) return;
    const url = buildWebSocketUrl(spotId);
    console.log(`[LiveSync] Connecting to ${url}`);
    try {
      ws = new WebSocket(url);
    } catch (err) {
      console.error("[LiveSync] WebSocket construct failed:", err);
      scheduleReconnect();
      return;
    }
    ws.binaryType = "arraybuffer";

    ws.addEventListener("open", () => {
      console.log("[LiveSync] Connected, authenticating");
      reconnectAttempts = 0;
      try {
        const currentToken = getCurrentToken();
        ws!.send(
          JSON.stringify({
            type: "auth",
            token: currentToken,
            sessionId,
            spotId,
            clientType: "viewer",
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "viewer",
          })
        );
      } catch (err) {
        console.error("[LiveSync] Auth send failed:", err);
      }
    });

    ws.addEventListener("message", (event: MessageEvent) => {
      let message: SessionMessage;
      try {
        const raw = event.data;
        if (typeof raw === "string") {
          message = JSON.parse(raw) as SessionMessage;
        } else if (raw instanceof ArrayBuffer) {
          try {
            const text = new TextDecoder().decode(raw);
            message = JSON.parse(text) as SessionMessage;
          } catch {
            return;
          }
        } else {
          return;
        }
      } catch (err) {
        console.warn("[LiveSync] Unparseable message:", err);
        return;
      }

      console.log(`[LiveSync] <- ${message?.type}`, message);

      switch (message?.type) {
        case "auth_success":
          console.log("[LiveSync] Authenticated");
          startHeartbeat();
          break;
        case "auth_error":
          console.error("[LiveSync] Auth failed:", message.error);
          isClosed = true;
          ws!.close();
          break;
        case "scene_draft_updated":
          triggerUpdate("scene");
          break;
        case "node_graph_draft_updated":
          triggerUpdate("nodes");
          break;
        case "promote_status":
          if (message.status === "done") triggerUpdate("promote");
          break;
        default:
          break;
      }
    });

    ws.addEventListener("close", (event) => {
      stopHeartbeat();
      console.log(`[LiveSync] Disconnected (code=${event.code})`);
      if (!isClosed && event.code !== 1000) {
        scheduleReconnect();
      }
    });

    ws.addEventListener("error", (err) => {
      console.warn("[LiveSync] Socket error:", err);
    });
  };

  connect();

  unsubscribeTokenChange = onTokenChange((newToken, reason) => {
    if (isClosed || !newToken || reason !== "refresh") return;
    console.log("[LiveSync] Token refreshed - reconnecting WebSocket");
    try {
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close(4000, "token refresh");
      }
    } catch (err) {
      console.warn("[LiveSync] Error closing WS during token refresh:", err);
    }
    reconnectAttempts = 0;
  });

  return {
    close() {
      isClosed = true;
      stopHeartbeat();
      if (reloadTimer) clearTimeout(reloadTimer);
      if (unsubscribeTokenChange) {
        try {
          unsubscribeTokenChange();
        } catch (err) {
          console.warn("[LiveSync] Token unsubscribe failed:", err);
        }
        unsubscribeTokenChange = null;
      }
      if (ws) ws.close(1000, "client closed");
    },
  };
}
